import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../store/index.ts'
import { addTable } from '../../../store/seatingSlice.ts'
import { TableShape } from '../../../interface/index.ts'
import './index.css'

interface Props {
	weddingTitle?: string;
	onLogout?: () => void;
	isSaving?: boolean;
	// Refs for onboarding
	addTableButtonsRef?: React.RefObject<HTMLDivElement>;
	// Mobile props
	isMobile?: boolean;
}

const SeatingToolbar: React.FC<Props> = ({
	weddingTitle,
	onLogout,
	isSaving = false,
	addTableButtonsRef,
	isMobile = false
}) => {
	const { t } = useTranslation('planner')
	const dispatch = useAppDispatch()
	const { seatingPlan, pendingChanges } = useAppSelector(({ seating }) => seating)
	const [isMenuOpen, setIsMenuOpen] = useState(false)

	const generateId = () => {
		return `table-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
	}

	const handleAddTable = (shape: TableShape) => {
		const tableCount = seatingPlan?.tables.length || 0
		const canvas = document.querySelector('.floor-plan-canvas')
		let centerX = 50
		let centerY = 50

		if (canvas) {
			const rect = canvas.getBoundingClientRect()
			const viewportCenterX = rect.left + rect.width / 2
			const viewportCenterY = rect.top + rect.height / 2
			const canvasArea = canvas.querySelector('.floor-plan-area')
			if (canvasArea) {
				const areaRect = canvasArea.getBoundingClientRect()
				centerX = ((viewportCenterX - areaRect.left) / areaRect.width) * 100
				centerY = ((viewportCenterY - areaRect.top) / areaRect.height) * 100
			}
		}

		const newTable = {
			id: generateId(),
			name: `${t('app.table')} ${tableCount + 1}`,
			shape,
			capacity: shape === 'circle' ? 8 : 10,
			position: { x: centerX, y: centerY },
			rotation: 0
		}

		dispatch(addTable(newTable))
		setIsMenuOpen(false)
	}

	// Mobile layout
	if (isMobile) {
		return (
			<div className='seating-toolbar mobile'>
				{weddingTitle && (
					<div className='toolbar-section toolbar-title'>
						<h2 className='wedding-title'>{weddingTitle}</h2>
					</div>
				)}

				<div className='toolbar-section'>
					<span className={`save-status ${isSaving ? 'saving' : pendingChanges ? 'pending' : 'saved'}`}>
						{isSaving ? t('toolbar.saving') : pendingChanges ? t('toolbar.unsaved') : t('toolbar.saved')}
					</span>

					<button
						className='mobile-menu-btn'
						onClick={() => setIsMenuOpen(!isMenuOpen)}
						aria-label='Menu'
					>
						☰
					</button>
				</div>

				{/* Mobile dropdown menu - Add table moved to FAB on canvas */}
				{isMenuOpen && (
					<>
						<div className='mobile-menu-backdrop' onClick={() => setIsMenuOpen(false)} />
						<div className='mobile-menu'>
							{onLogout && (
								<div className='mobile-menu-section'>
									<button
										className='mobile-menu-btn-item logout'
										onClick={() => {
											setIsMenuOpen(false)
											onLogout()
										}}
									>
										{t('toolbar.logout')}
									</button>
								</div>
							)}
						</div>
					</>
				)}
			</div>
		)
	}

	// Desktop layout
	return (
		<div className='seating-toolbar'>
			{weddingTitle && (
				<div className='toolbar-section toolbar-title'>
					<h2 className='wedding-title'>{weddingTitle}</h2>
				</div>
			)}

			<div ref={addTableButtonsRef} className='toolbar-section'>
				<span className='toolbar-label'>{t('toolbar.addTable')}</span>
				<button
					className='toolbar-btn'
					onClick={() => handleAddTable('circle')}
					title={t('toolbar.addRoundTable')}
				>
					<svg viewBox='0 0 24 24' width='20' height='20'>
						<circle cx='12' cy='12' r='10' fill='none' stroke='currentColor' strokeWidth='2' />
					</svg>
					{t('toolbar.round')}
				</button>
				<button
					className='toolbar-btn'
					onClick={() => handleAddTable('rectangle')}
					title={t('toolbar.addRectangularTable')}
				>
					<svg viewBox='0 0 24 24' width='20' height='20'>
						<rect x='2' y='6' width='20' height='12' fill='none' stroke='currentColor' strokeWidth='2' rx='2' />
					</svg>
					{t('toolbar.rectangular')}
				</button>
			</div>

			<div className='toolbar-section'>
				<span className={`save-status ${isSaving ? 'saving' : pendingChanges ? 'pending' : 'saved'}`}>
					{isSaving ? t('toolbar.saving') : pendingChanges ? t('toolbar.unsaved') : t('toolbar.saved')}
				</span>

				{onLogout && (
					<button
						className='toolbar-btn logout-btn'
						onClick={onLogout}
					>
						{t('toolbar.logout')}
					</button>
				)}
			</div>
		</div>
	)
}

export default SeatingToolbar
